import { Task } from "@dhx/react-gantt";
import { SchedulingItem, SchedulingStatus } from "../types/scheduling";
import dayjs from "dayjs";

export const transformSchedulingItemToTask = (item: SchedulingItem): Task => {
  const type =
    item.type === "phase"
      ? "project"
      : item.type === "stage_gate"
        ? "milestone"
        : "task";

  // Parse all four dates
  const parsedPlannedStart = item.planned_start_date ? dayjs(item.planned_start_date).startOf('day') : null;
  const parsedPlannedEnd = item.planned_end_date ? dayjs(item.planned_end_date).startOf('day') : null;
  const parsedActualEnd = item.actual_end_date ? dayjs(item.actual_end_date).startOf('day') : null;

  const progress = parseFloat(item.progress_percentage) / 100 || 0;

  // Determine bar dates, based on planned dates
  const barStart = parsedPlannedStart ?? parsedPlannedStart;
  const barEnd = parsedPlannedEnd ?? parsedPlannedEnd;

  // If no dates at all, mark as unscheduled — Gantt shows it in the grid
  // but doesn't position it on the timeline
  if (!barStart && !barEnd) {
    const unscheduledTask: Task = {
      id: item.id,
      text: item.name,
      type,
      unscheduled: true,
      duration: 0,
      progress,
      parent: item.parent_id || 0,
      open: true,
    };
    // Attach all dates for potential use elsewhere
    (unscheduledTask as any).planned_start_date = parsedPlannedStart ? parsedPlannedStart.toDate() : undefined;
    (unscheduledTask as any).planned_end_date = parsedPlannedEnd ? parsedPlannedEnd.toDate() : undefined;
    (unscheduledTask as any).actual_end_date = parsedActualEnd ? parsedActualEnd.toDate() : undefined;
    return unscheduledTask;
  }

  // Use whichever date is available for the missing one
  const startDayjs = barStart ?? barEnd!;
  const endDayjs = barEnd ?? barStart!;

  // +1 because both start and end dates are inclusive
  // e.g. start=23rd, end=24th → 2 days; same date → 1 day
  const duration = Math.max(
    1,
    endDayjs.diff(startDayjs, 'day') + 1
  );

  const task: Task = {
    id: item.id,
    text: item.name,
    type,
    start_date: startDayjs.toDate(),
    duration,
    progress,
    parent: item.parent_id || 0,
    open: true,
    ...(type === "project" ? { auto_scheduling: false } : {}),
  };

  // Attach all four dates as custom properties for renderers/tooltips
  (task as any).planned_start_date = parsedPlannedStart ? parsedPlannedStart.toDate() : undefined;
  (task as any).planned_end_date = parsedPlannedEnd ? parsedPlannedEnd.toDate() : undefined;
  (task as any).actual_end_date = parsedActualEnd ? parsedActualEnd.toDate() : undefined;

  return task;
};

export const transformSchedulingItemsToTasks = (
  items: SchedulingItem[]
): Task[] => {
  return items.map(transformSchedulingItemToTask);
};

export const getStatusFromProgress = (progress: number): SchedulingStatus => {
  if (progress === 0) return "not_started";
  if (progress === 100) return "completed";
  return "in_progress";
};

export const formatDateForAPI = (date: Date | string | dayjs.Dayjs): string => {
  return dayjs(date).startOf('day').toISOString();
};

export const calculateEndDate = (startDate: Date, duration: number): Date => {
  const start = dayjs(startDate).startOf('day');
  return start.add(duration, 'day').toDate();
};

export const calculateDuration = (startDate: Date, endDate: Date): number => {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  return Math.max(1, end.diff(start, 'day') + 1);
};
